import React, {useEffect, useRef} from 'react';
import {FormControl, Stack, Typography, capitalize, useTheme} from '@mui/material';

import Icon from 'presentation/icons';
import IconNamesType from 'presentation/icons/IconNamesType';
import {translations} from 'presentation/translations-new';
import {useMediaQueries} from 'presentation/pages/private/hooks';
import {FiveStarsEnum} from 'main/analytics/gaTagsEnum';
import {
	StyledCard,
	StyledRadio,
	StyledFormControlLabel,
	StyledLabel,
	StyledRadioGroup,
	StyledMotivatorTitle,
} from './styles';
import {MotivatorsProps} from './types';

const {rateToProceed} = translations['pt-br'].privateFlow.fiveStarsRating;

export function Motivators({motivator, hasError, ...props}: MotivatorsProps) {
	const {palette} = useTheme();
	const errorRef = useRef<HTMLDivElement>(null);
	const {isLaptop} = useMediaQueries();

	function getColorMapping(numOptions: number): Record<number, string> {
		if (numOptions === 3) {
			return {
				0: palette.success.main,
				1: palette.error.main,
				2: '',
			};
		}
		if (numOptions === 2) {
			return {
				0: palette.success.main,
				1: palette.error.main,
			};
		}
		return {};
	}

	function getIconMapping(numOptions: number): Record<number, string> {
		if (numOptions === 3) {
			return {
				0: 'check-default',
				1: 'close-filled',
				2: '',
			};
		}
		if (numOptions === 2) {
			return {
				0: 'check-default',
				1: 'close-filled',
			};
		}
		return {};
	}

	const mappedColorWithThreeOptions = getColorMapping(3);
	const mappedColorWithTwoOptions = getColorMapping(2);

	const mappedColor =
		motivator.possiveis_respostas.length === 3
			? mappedColorWithThreeOptions
			: mappedColorWithTwoOptions;

	const mappedIconNameWithThreeOptions = getIconMapping(3);
	const mappedIconNameWithTwoOptions = getIconMapping(2);

	const mappedIconName =
		motivator.possiveis_respostas.length === 3
			? mappedIconNameWithThreeOptions
			: mappedIconNameWithTwoOptions;

	useEffect(() => {
		if (hasError && errorRef.current) {
			const {top, bottom} = errorRef.current.getBoundingClientRect();
			const isInView = top >= 200 && bottom <= window.innerHeight;

			if (!isInView) {
				errorRef.current.scrollIntoView({behavior: 'smooth', block: 'center'});
			}
		}
	}, [hasError]);

	const notApplyMotivatorOrder = isLaptop ? 2 : -1;

	return (
		<Stack data-tag-id={FiveStarsEnum.QUANTIDADE_PERGUNTAS}>
			<StyledCard ref={errorRef} $isError={hasError}>
				<StyledMotivatorTitle>{motivator.descricao}</StyledMotivatorTitle>
				<FormControl>
					<StyledRadioGroup {...props}>
						{motivator.possiveis_respostas.map((answer, index) => (
							<StyledFormControlLabel
								data-tag-id={FiveStarsEnum.AVALIACAO_PERGUNTAS}
								sx={{
									order:
										motivator.possiveis_respostas.length === 3 && index === 2
											? notApplyMotivatorOrder
											: 0,
								}}
								key={`${answer}-${index}`}
								data-index={motivator.possiveis_respostas.length === 3 ? index : -1}
								data-value={props.value === answer}
								value={answer}
								control={<StyledRadio />}
								label={
									<StyledLabel>
										<Icon
											name={mappedIconName[index] as IconNamesType}
											color={mappedColor[index]}
										/>
										<Typography color={palette.grey.A700} noWrap>
											{capitalize(answer)}
										</Typography>
									</StyledLabel>
								}
							/>
						))}
					</StyledRadioGroup>
				</FormControl>
			</StyledCard>
			{hasError ? (
				<Typography marginLeft={1.5} variant="helperText" color={palette.error.main}>
					{rateToProceed}
				</Typography>
			) : null}
		</Stack>
	);
}
